<template>
  <article class="flex flex-col gap-1">
    <button
      v-if="props.canSelect && canUse"
      type="button"
      data-build-action="select-guild"
      :data-active-build="props.active ? 'true' : undefined"
      :class="[
        'group grid grid-cols-[auto_minmax(0,1fr)_auto] items-center gap-2 w-full px-2 py-1.5 rounded-sm border text-left transition-[border-color,background-color] duration-120 ease-[cubic-bezier(0.4,0,0.2,1)] focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-accent',
        props.active
          ? 'border-accent bg-accent/10'
          : 'border-transparent hover:border-border hover:bg-bg-surface',
      ]"
      :title="useGuildBuildTitle"
      @click="onUse"
    >
      <span class="inline-flex items-center gap-0.5 p-1 rounded-sm bg-bg-surface border border-border shrink-0">
        <template v-for="(slug, slotIdx) in slotLabels" :key="`${props.entry.id}-${slotIdx}`">
          <img
            v-if="weaponIcon(slug)"
            :src="weaponIcon(slug)"
            :alt="localizedWeaponName(slug)"
            class="w-6 h-6 object-cover rounded-sm shrink-0"
            draggable="false"
          >
          <span v-else class="w-6 h-6 inline-flex items-center justify-center text-xs font-bold text-text-muted border border-dashed border-border-strong rounded-sm shrink-0" aria-hidden="true">+</span>
        </template>
      </span>
      <span class="min-w-0">
        <span class="flex min-w-0 flex-wrap items-center gap-1.5 font-mono text-xs font-semibold text-text-primary">
          <span class="shrink-0">{{ messages.builds.guildBuildLabel(props.entry.orderIndex + 1) }}</span>
          <BuildStageBadges :stage-names="props.stageNames" />
        </span>
        <span class="block mt-0.5 text-xs leading-tight text-text-muted truncate">{{ buildSummary }}</span>
      </span>
      <span v-if="props.active" class="inline-flex items-center gap-1 text-xs font-semibold tracking-[0.04em] uppercase text-accent shrink-0">
        <span aria-hidden="true">✓</span>
        <span>{{ messages.builds.activeLabel }}</span>
      </span>
      <span v-else class="text-sm text-text-muted opacity-0 group-hover:opacity-100" aria-hidden="true">→</span>
    </button>
    <div
      v-else
      :class="[
        'grid grid-cols-[auto_minmax(0,1fr)_auto] items-center gap-2 w-full px-2 py-1.5 rounded-sm border text-left',
        props.active ? 'border-accent bg-accent/10' : 'border-transparent',
      ]"
    >
      <span class="inline-flex items-center gap-0.5 p-1 rounded-sm bg-bg-surface border border-border shrink-0">
        <template v-for="(slug, slotIdx) in slotLabels" :key="`read-${props.entry.id}-${slotIdx}`">
          <img v-if="weaponIcon(slug)" :src="weaponIcon(slug)" :alt="localizedWeaponName(slug)" class="w-6 h-6 object-cover rounded-sm shrink-0" draggable="false">
          <span v-else class="w-6 h-6 inline-flex items-center justify-center text-xs font-bold text-text-muted border border-dashed border-border-strong rounded-sm shrink-0" aria-hidden="true">+</span>
        </template>
      </span>
      <span class="min-w-0">
        <span class="flex min-w-0 flex-wrap items-center gap-1.5 font-mono text-xs font-semibold text-text-primary">
          <span class="shrink-0">{{ messages.builds.guildBuildLabel(props.entry.orderIndex + 1) }}</span>
          <BuildStageBadges :stage-names="props.stageNames" />
        </span>
        <span class="block mt-0.5 text-xs leading-tight text-text-muted truncate">{{ buildSummary }}</span>
      </span>
      <span v-if="!isSelectable" class="text-xs font-semibold text-text-muted shrink-0">{{ messages.builds.requiresDistinctWeapons }}</span>
      <span v-else-if="!props.entry.isAssigned" class="text-xs font-semibold text-text-muted shrink-0">{{ messages.builds.notAssigned }}</span>
    </div>
    <div v-if="props.canManage || (props.canAssign && !props.entry.isAssigned)" class="flex items-center justify-end gap-1 px-1">
      <button v-if="props.canAssign && !props.entry.isAssigned" type="button" data-build-action="assign-guild" class="px-1.5 py-0.5 text-xs font-semibold text-accent hover:text-accent/80 focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-accent" @click="onAssign">{{ messages.builds.assignToPlayer }}</button>
      <button v-if="props.canManage" type="button" data-build-action="edit-guild" class="px-1.5 py-0.5 text-xs text-text-secondary hover:text-accent focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-accent" @click="emit('edit', 0)">{{ messages.builds.editFirst }}</button>
      <button v-if="props.canManage" type="button" data-build-action="edit-guild" class="px-1.5 py-0.5 text-xs text-text-secondary hover:text-accent focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-accent" @click="emit('edit', 1)">{{ messages.builds.editSecond }}</button>
      <button v-if="props.canManage" type="button" data-build-action="remove-guild" class="px-1.5 py-0.5 text-xs text-danger hover:text-danger/80 focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-accent" @click="emit('remove')">{{ messages.builds.remove }}</button>
    </div>
  </article>
</template>

<script setup lang="ts">
import { computed } from 'vue'

import { WEAPONS_BY_SLUG } from '@/data/weapons'
import { isDistinctWeaponPair } from '../../lib/playerWeaponBuilds'
import { weaponDisplayName } from '../../lib/roleLabels'
import { defineLocaleCatalog } from '@/shared/i18n'
import { useLocaleMessages } from '@/shared/composables/useI18n'
import type { GuildBuildEntry } from '../../types'
import BuildStageBadges from './BuildStageBadges.vue'
import { weaponsEn } from './locales/en.ts'
import { weaponsRu } from './locales/ru.ts'

export interface PlayerGuildBuildRowProps {
  readonly entry: GuildBuildEntry
  readonly active: boolean
  readonly stageNames: ReadonlyArray<string>
  readonly canSelect: boolean
  readonly canUseUnassigned: boolean
  readonly canAssign: boolean
  readonly canManage: boolean
}

const props = defineProps<PlayerGuildBuildRowProps>()
const catalog = defineLocaleCatalog({ en: weaponsEn, ru: weaponsRu })
const messages = useLocaleMessages(catalog)
const emit = defineEmits<{
  (event: 'use'): void
  (event: 'assign'): void
  (event: 'edit', slot: 0 | 1): void
  (event: 'remove'): void
}>()

const isSelectable = computed<boolean>(() => isDistinctWeaponPair(props.entry.weaponSlugs))
const canUse = computed<boolean>(
  () => isSelectable.value && (props.entry.isAssigned || props.canUseUnassigned),
)
const slotLabels = computed<ReadonlyArray<string | undefined>>(() => {
  const weapons = props.entry.weaponSlugs
  if (weapons.length >= 2) return [weapons[0], weapons[1]]
  if (weapons.length === 1) return [weapons[0], undefined]
  return [undefined, undefined]
})
const buildSummary = computed<string>(() => {
  const names = props.entry.weaponSlugs
    .map((slug) => weaponName(slug))
    .filter((name): name is string => Boolean(name))
  return names.length > 0 ? names.join(' + ') : messages.value.builds.noWeaponsSelected
})

const useGuildBuildTitle = computed<string>(() => messages.value.builds.useGuildBuildTitle(buildSummary.value))

function localizedWeaponName(slug: string | undefined): string {
  if (!slug) return ''
  const weapon = WEAPONS_BY_SLUG.get(slug)
  if (weapon === undefined) return ''
  return weaponDisplayName(weapon.slug, weapon.name, messages.value.names)
}

function weaponName(slug: string | undefined): string | undefined {
  const label = localizedWeaponName(slug)
  return label === '' ? undefined : label
}

function weaponIcon(slug: string | undefined): string | undefined {
  return slug ? WEAPONS_BY_SLUG.get(slug)?.icon : undefined
}

function onUse(): void {
  if (props.canSelect && canUse.value) emit('use')
}

function onAssign(): void {
  if (props.canAssign && !props.entry.isAssigned) emit('assign')
}
</script>
